import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Building2, Clock, Wrench, UserCheck, Plus, Pencil, Trash2, Timer, AlertTriangle, Mail, Car, FileText, Eye, Gauge, BarChart3, ChevronUp, ChevronDown, GripVertical, Shield, Check, X, ClipboardList, Search, Filter, Zap, ArrowUp, Monitor, Image as ImageIcon, ExternalLink, Power, Upload } from "lucide-react";
import { validateHoursFormat } from "@/utils/slots";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERVAL_OPTIONS = [10, 15, 20, 30, 45, 60];

/* ═══════════════════════════════════════════
   SECTIONS METADATA & CATEGORIES
   ═══════════════════════════════════════════ */
const SECTIONS_META = [
  {
    id: "datos-taller", title: "Datos del Taller", icon: "Building2", category: "general",
    keywords: ["nombre", "email", "telefono", "direccion", "empresa", "logo", "iniciales", "tagline", "eslogan", "legal", "footer", "redes sociales", "facebook", "instagram", "twitter", "linkedin", "youtube", "web", "company", "comercial", "domicilio", "whatsapp", "whatsapp general"]
  },
  {
    id: "horario-trabajo", title: "Horario de Trabajo", icon: "Clock", category: "general",
    keywords: ["horario", "horas", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo", "apertura", "cierre", "cerrado", "jornada", "turno", "partido"]
  },
  {
    id: "servicios", title: "Servicios Ofrecidos", icon: "Wrench", category: "servicios",
    keywords: ["servicio", "recepcion", "rapida", "formulario", "publico", "orden", "activar", "desactivar", "taller", "mecanica", "revision", "itv", "neumaticos"]
  },
  {
    id: "intervalo-citas", title: "Intervalo de Citas", icon: "Timer", category: "servicios",
    keywords: ["cita", "intervalo", "minutos", "tiempo", "huecos", "horarios", "franja", "slot", "duracion"]
  },
  {
    id: "marcas", title: "Marcas Oficiales", icon: "Car", category: "vehiculos",
    keywords: ["marca", "logo", "concesionario", "oficial", "stellantis", "peugeot", "citroen", "fiat", "opel", "ds", "jeep", "alfa"]
  },
  {
    id: "umbrales-alertas", title: "Umbrales de Alertas y Dashboard", icon: "AlertTriangle", category: "negocio",
    keywords: ["umbral", "saturacion", "huecos", "muertos", "presion", "agenda", "pendientes", "alerta", "dashboard", "indicador", "porcentaje"]
  },
  {
    id: "negocio", title: "Negocio / Gerencia", icon: "Gauge", category: "negocio",
    keywords: ["ticket", "medio", "ingresos", "objetivo", "mensual", "vip", "cliente", "gerencia", "prevision", "facturacion", "euros", "dinero", "rentabilidad"]
  },
  {
    id: "email", title: "Notificaciones por Email", icon: "Mail", category: "comunicaciones",
    keywords: ["email", "correo", "notificacion", "recepcion", "confirmacion", "recordatorio", "horas", "antelacion", "enviar", "smtp", "mensaje"]
  },
  {
    id: "vehiculos-config", title: "Configuracion de Vehiculos", icon: "Car", category: "vehiculos",
    keywords: ["vehiculo", "coche", "estado", "disponible", "reservado", "vendido", "tipo", "km0", "usado", "ocasion", "etiqueta", "stock", "catalogo", "combustible", "transmision"]
  },
  {
    id: "mensajes-formulario", title: "Mensajes del Formulario de Reserva", icon: "FileText", category: "comunicaciones",
    keywords: ["formulario", "mensaje", "exito", "reserva", "legal", "cerrado", "horario", "texto", "confirmacion", "booking"]
  },
  {
    id: "carga-agenda", title: "Indicador de Carga de Agenda", icon: "BarChart3", category: "servicios",
    keywords: ["carga", "agenda", "verde", "amarillo", "rojo", "ocupacion", "habitual", "cliente", "visitas", "indicador", "calendario"]
  },
  {
    id: "home-publica", title: "Home Publica / Hero", icon: "Monitor", category: "apariencia",
    keywords: ["home", "hero", "slider", "banner", "slide", "carrusel", "imagen", "campana", "promocion", "principal", "autoplay", "rotatorio", "landing"]
  },
  {
    id: "secciones-web", title: "Secciones Visibles en la Web", icon: "Eye", category: "apariencia",
    keywords: ["seccion", "web", "hero", "banner", "marcas", "servicios", "ventajas", "testimonios", "vehiculos", "centros", "cta", "visible", "ocultar", "mostrar", "landing", "home"]
  },
  {
    id: "informes-pdf", title: "Configuracion de Informes PDF", icon: "Gauge", category: "apariencia",
    keywords: ["informe", "pdf", "reporte", "empresa", "pie", "footer", "exportar", "imprimir", "documento"]
  },
  {
    id: "privacidad", title: "Privacidad y Proteccion de Datos", icon: "Shield", category: "legal",
    keywords: ["privacidad", "rgpd", "proteccion", "datos", "responsable", "cif", "cookie", "cookies", "banner", "consentimiento", "legal", "politica", "formulario", "checkbox", "comercial", "whatsapp", "asesor", "contacto", "lopd", "gdpr", "aceptar", "rechazar", "configurar"]
  },
  {
    id: "asesores", title: "Asesores de Servicio", icon: "UserCheck", category: "personal",
    keywords: ["asesor", "servicio", "foto", "centro", "activo", "seleccion", "preferencia", "posventa", "taller", "vendedor", "personal", "empleado"]
  },
  {
    id: "auditoria", title: "Registro de Auditoria", icon: "ClipboardList", category: "auditoria",
    keywords: ["auditoria", "registro", "log", "actividad", "actor", "accion", "ip", "fecha", "historial", "seguridad", "trazabilidad"]
  },
  {
    id: "roles", title: "Roles y Permisos", icon: "Shield", category: "personal",
    keywords: ["rol", "permiso", "modulo", "acceso", "nivel", "admin", "sistema", "seguridad", "usuario"]
  }
];

const CATEGORIES = [
  { key: "all", label: "Todos" },
  { key: "general", label: "General" },
  { key: "servicios", label: "Servicios y Citas" },
  { key: "vehiculos", label: "Vehículos y Ventas" },
  { key: "comunicaciones", label: "Comunicaciones" },
  { key: "legal", label: "Legal / RGPD" },
  { key: "apariencia", label: "Apariencia" },
  { key: "negocio", label: "Negocio" },
  { key: "personal", label: "Personal" },
  { key: "auditoria", label: "Auditoria" }
];

const QUICK_ACCESS = [
  { id: "datos-taller", label: "Datos" },
  { id: "horario-trabajo", label: "Horario" },
  { id: "home-publica", label: "Home" },
  { id: "privacidad", label: "RGPD" },
  { id: "email", label: "Emails" },
  { id: "asesores", label: "Asesores" },
  { id: "vehiculos-config", label: "Vehículos" },
];

const normalize = (text) => text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function SettingsPage() {
  const { axiosAuth } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [advDialog, setAdvDialog] = useState(false);
  const [editAdv, setEditAdv] = useState(null);
  const [advForm, setAdvForm] = useState({ name: "", center: "ambos", active: true, order: 0, email: "", phone: "", title: "" });
  const [currentAdvisorHasManualPhoto, setCurrentAdvisorHasManualPhoto] = useState(false);
  const [centers, setCenters] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [roleDialog, setRoleDialog] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", slug: "", level: 10, permissions: {}, active: true });
  const [roleSaveConfirm, setRoleSaveConfirm] = useState(false);
  const [roleDeleteConfirm, setRoleDeleteConfirm] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [auditActorFilter, setAuditActorFilter] = useState("all");
  const [auditTargetFilter, setAuditTargetFilter] = useState("all");
  const [auditDateFilter, setAuditDateFilter] = useState("all");
  const [auditIpFilter, setAuditIpFilter] = useState("");
  const [auditSortOrder, setAuditSortOrder] = useState("desc");
  const [auditAvailableActions, setAuditAvailableActions] = useState([]);
  const [auditAvailableActors, setAuditAvailableActors] = useState([]);

  // Hero slides state
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroSlideDialog, setHeroSlideDialog] = useState(false);
  const [editSlide, setEditSlide] = useState(null);
  const [slideForm, setSlideForm] = useState({});

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [highlightedSection, setHighlightedSection] = useState(null);
  const searchInputRef = useRef(null);
  const highlightTimeoutRef = useRef(null);

  const visibleSections = useMemo(() => {
    const query = normalize(searchQuery.trim());
    return new Set(
      SECTIONS_META.filter(section => {
        if (activeCategory !== "all" && section.category !== activeCategory) return false;
        if (!query) return true;
        const haystack = normalize([section.title, ...section.keywords].join(" "));
        return query.split(/\s+/).every(word => haystack.includes(word));
      }).map(s => s.id)
    );
  }, [searchQuery, activeCategory]);

  const scrollToSection = useCallback((sectionId) => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      setHighlightedSection(sectionId);
      highlightTimeoutRef.current = setTimeout(() => setHighlightedSection(null), 2500);
    }
  }, []);

  // Auto-scroll when single result
  useEffect(() => {
    if (searchQuery.trim() && visibleSections.size === 1) {
      const sectionId = [...visibleSections][0];
      const timer = setTimeout(() => scrollToSection(sectionId), 200);
      return () => clearTimeout(timer);
    }
  }, [visibleSections, searchQuery, scrollToSection]);

  const clearSearch = () => {
    setSearchQuery("");
    setActiveCategory("all");
    searchInputRef.current?.focus();
  };

  const isSectionVisible = (id) => visibleSections.has(id);

  const sectionClass = (id) =>
    `transition-all duration-500 ${highlightedSection === id ? "ring-2 ring-red-500/60 ring-offset-4 rounded-sm" : ""}`;

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/config");
      setConfig(res.data);
    } catch {
      toast.error("Error al cargar configuracion");
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  const fetchAdvisors = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/advisors");
      setAdvisors(res.data);
    } catch {}
  }, [axiosAuth]);

  const fetchCenters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/centers`);
      setCenters(res.data.filter(c => c.active));
    } catch {}
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/roles");
      setRoles(res.data);
    } catch {}
  }, [axiosAuth]);

  const fetchModules = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/roles/modules");
      setModules(res.data.modules || []);
      setActions(res.data.actions || []);
    } catch {}
  }, [axiosAuth]);

  const fetchAudit = useCallback(async (pg = 1) => {
    try {
      const params = new URLSearchParams();
      params.set("page", String(pg));
      params.set("limit", "30");
      params.set("sort_order", auditSortOrder);
      if (auditSearch) params.set("search", auditSearch);
      if (auditActionFilter !== "all") params.set("action", auditActionFilter);
      if (auditActorFilter !== "all") params.set("actor_id", auditActorFilter);
      if (auditTargetFilter !== "all") params.set("target_type", auditTargetFilter);
      if (auditIpFilter) params.set("ip", auditIpFilter);
      if (auditDateFilter !== "all") {
        const now = new Date();
        if (auditDateFilter === "today") params.set("date_from", now.toISOString().slice(0, 10));
        else if (auditDateFilter === "7d") params.set("date_from", new Date(now - 7 * 86400000).toISOString().slice(0, 10));
        else if (auditDateFilter === "30d") params.set("date_from", new Date(now - 30 * 86400000).toISOString().slice(0, 10));
      }
      const res = await axiosAuth().get(`/audit?${params.toString()}`);
      setAuditLogs(res.data.items || []);
      setAuditTotal(res.data.total || 0);
      setAuditPage(pg);
      if (res.data.available_actions) setAuditAvailableActions(res.data.available_actions);
      if (res.data.available_actors) setAuditAvailableActors(res.data.available_actors);
    } catch {}
  }, [axiosAuth, auditSearch, auditActionFilter, auditActorFilter, auditTargetFilter, auditDateFilter, auditIpFilter, auditSortOrder]);

  const fetchHeroSlides = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/hero-slides");
      setHeroSlides(res.data);
    } catch {}
  }, [axiosAuth]);

  useEffect(() => { fetchConfig(); fetchAdvisors(); fetchCenters(); fetchRoles(); fetchModules(); fetchAudit(); fetchHeroSlides(); }, [fetchConfig, fetchAdvisors, fetchCenters, fetchRoles, fetchModules, fetchAudit, fetchHeroSlides]);

  const createSlide = async () => {
    try {
      await axiosAuth().post("/hero-slides");
      toast.success("Slide creado");
      fetchHeroSlides();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const saveSlide = async () => {
    if (!editSlide) return;
    try {
      await axiosAuth().put(`/hero-slides/${editSlide}`, slideForm);
      toast.success("Slide actualizado");
      setHeroSlideDialog(false);
      setEditSlide(null);
      fetchHeroSlides();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const deleteSlide = async (id) => {
    if (!window.confirm("Eliminar este slide?")) return;
    try {
      await axiosAuth().delete(`/hero-slides/${id}`);
      toast.success("Slide eliminado");
      fetchHeroSlides();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleSlideActive = async (slide) => {
    try {
      await axiosAuth().put(`/hero-slides/${slide.id}`, { active: !slide.active });
      fetchHeroSlides();
    } catch {}
  };

  const openEditSlide = (s) => {
    setEditSlide(s.id);
    setSlideForm({
      title: s.title || "", subtitle: s.subtitle || "", aux_text: s.aux_text || "",
      cta_primary_text: s.cta_primary_text || "", cta_primary_link: s.cta_primary_link || "",
      cta_secondary_text: s.cta_secondary_text || "", cta_secondary_link: s.cta_secondary_link || "",
      image_url: s.image_url || "", image_mobile_url: s.image_mobile_url || "",
      text_color: s.text_color || "white", overlay_opacity: s.overlay_opacity ?? 70,
      order: s.order ?? 0, active: s.active ?? true,
    });
    setHeroSlideDialog(true);
  };

  useEffect(() => { setAuditPage(1); }, [auditSearch, auditActionFilter, auditActorFilter, auditTargetFilter, auditDateFilter, auditIpFilter, auditSortOrder]);
  useEffect(() => { const t = setTimeout(() => fetchAudit(auditPage), 300); return () => clearTimeout(t); }, [fetchAudit, auditPage]);

  const handleSave = async () => {
    const errors = Object.values(hourErrors).filter(Boolean);
    if (errors.length > 0) {
      toast.error("Corrige los horarios antes de guardar");
      return;
    }
    setSaving(true);
    try {
      await axiosAuth().put("/config", config);
      toast.success("Configuracion guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdvisor = async () => {
    try {
      if (editAdv) {
        await axiosAuth().put(`/advisors/${editAdv}`, advForm);
        toast.success("Asesor actualizado");
      } else {
        await axiosAuth().post("/advisors", advForm);
        toast.success("Asesor creado");
      }
      setAdvDialog(false);
      setEditAdv(null);
      fetchAdvisors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleDeleteAdvisor = async (id) => {
    if (!window.confirm("Eliminar este asesor?")) return;
    try {
      await axiosAuth().delete(`/advisors/${id}`);
      toast.success("Asesor eliminado");
      fetchAdvisors();
    } catch { toast.error("Error al eliminar"); }
  };

  const openEditRole = (r) => {
    setEditRole(r.id);
    setRoleForm({ name: r.name, slug: r.slug, level: r.level, permissions: { ...r.permissions }, active: r.active });
    setRoleDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!roleSaveConfirm) { setRoleSaveConfirm(true); return; }
    try {
      if (editRole) {
        await axiosAuth().put(`/roles/${editRole}`, roleForm);
        toast.success("Rol actualizado");
      } else {
        await axiosAuth().post("/roles", roleForm);
        toast.success("Rol creado");
      }
      setRoleDialog(false);
      setEditRole(null);
      setRoleSaveConfirm(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar rol");
      setRoleSaveConfirm(false);
    }
  };

  const handleDeleteRole = async (id) => {
    if (!roleDeleteConfirm) { setRoleDeleteConfirm(id); return; }
    try {
      await axiosAuth().delete(`/roles/${id}`);
      toast.success("Rol eliminado");
      setRoleDeleteConfirm(null);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar rol");
      setRoleDeleteConfirm(null);
    }
  };

  const toggleRoleActive = async (r) => {
    try {
      await axiosAuth().put(`/roles/${r.id}`, { active: !r.active });
      toast.success(r.active ? "Rol desactivado" : "Rol activado");
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const togglePermission = (moduleKey, action) => {
    const perms = { ...roleForm.permissions };
    if (!perms[moduleKey]) perms[moduleKey] = [];
    const arr = [...perms[moduleKey]];
    if (arr.includes(action)) {
      perms[moduleKey] = arr.filter(a => a !== action);
      if (perms[moduleKey].length === 0) delete perms[moduleKey];
    } else {
      perms[moduleKey] = [...arr, action];
    }
    setRoleForm({ ...roleForm, permissions: perms });
  };

  const toggleModuleAll = (moduleKey) => {
    const perms = { ...roleForm.permissions };
    const current = perms[moduleKey] || [];
    if (current.length === actions.length) {
      delete perms[moduleKey];
    } else {
      perms[moduleKey] = [...actions];
    }
    setRoleForm({ ...roleForm, permissions: perms });
  };

  const openEditAdv = (a) => {
    setEditAdv(a.id);
    setAdvForm({ name: a.name, center: a.center || "ambos", active: a.active, order: a.order || 0, email: a.email || "", phone: a.phone || "", title: a.title || "" });
    setCurrentAdvisorHasManualPhoto(!!a.has_manual_photo);
    setAdvDialog(true);
  };

  const [hourErrors, setHourErrors] = useState({});

  const updateHour = (day, value) => {
    setConfig({ ...config, working_hours: { ...config.working_hours, [day]: value } });
    const result = validateHoursFormat(value);
    setHourErrors(prev => ({ ...prev, [day]: result.valid ? null : result.error }));
  };

  if (loading || !config) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-sm" />)}</div>;
  }

  const DAYS = [
    { key: "lunes", label: "Lunes" }, { key: "martes", label: "Martes" },
    { key: "miercoles", label: "Miercoles" }, { key: "jueves", label: "Jueves" },
    { key: "viernes", label: "Viernes" }, { key: "sabado", label: "Sabado" },
    { key: "domingo", label: "Domingo" }
  ];

  const isFiltering = searchQuery.trim() !== "" || activeCategory !== "all";
  const matchCount = visibleSections.size;
  const totalSections = SECTIONS_META.length;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* ═══ HEADER ═══ */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="settings-title">Configuracion</h1>
          <p className="text-sm text-slate-500 mt-1">Datos generales del taller</p>
        </div>
        <Button onClick={handleSave} disabled={saving}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-5"
          data-testid="save-config-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>

      {/* ═══ SEARCH BAR & FILTERS ═══ */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-sm p-4 shadow-sm" data-testid="settings-search-bar">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            ref={searchInputRef}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar ajuste... (ej: email, logo, privacidad, cookies, horario)"
            className="pl-10 pr-10 h-10 rounded-sm border-slate-200 text-sm"
            data-testid="settings-search-input"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors"
              data-testid="settings-search-clear"
            >
              <X className="w-3 h-3 text-slate-600" />
            </button>
          )}
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="settings-category-filters">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(activeCategory === cat.key ? "all" : cat.key)}
              className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm border transition-all ${
                activeCategory === cat.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
              }`}
              data-testid={`settings-filter-${cat.key}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Search results summary */}
        {isFiltering && (
          <div className="mt-3 flex items-center justify-between" data-testid="settings-search-summary">
            <div className="flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-500">
                {matchCount === 0
                  ? "Sin resultados"
                  : `${matchCount} de ${totalSections} secciones`}
              </span>
            </div>
            {matchCount > 0 && matchCount <= 5 && (
              <div className="flex flex-wrap gap-1">
                {SECTIONS_META.filter(s => visibleSections.has(s.id)).map(s => (
                  <button
                    key={s.id}
                    onClick={() => scrollToSection(s.id)}
                    className="text-[10px] px-2 py-1 bg-red-50 text-red-700 rounded-sm hover:bg-red-100 transition-colors font-medium"
                    data-testid={`settings-result-${s.id}`}
                  >
                    {s.title}
                  </button>
                ))}
              </div>
            )}
            <button onClick={clearSearch} className="text-[10px] text-red-600 hover:text-red-800 font-semibold uppercase tracking-wider" data-testid="settings-clear-all-filters">
              Limpiar
            </button>
          </div>
        )}

        {/* Quick access (only when not filtering) */}
        {!isFiltering && (
          <div className="mt-3 flex items-center gap-2" data-testid="settings-quick-access">
            <Zap className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold flex-shrink-0">Acceso rapido</span>
            <div className="flex flex-wrap gap-1">
              {QUICK_ACCESS.map(qa => (
                <button
                  key={qa.id}
                  onClick={() => scrollToSection(qa.id)}
                  className="text-[10px] px-2.5 py-1 bg-slate-100 text-slate-600 rounded-sm hover:bg-slate-200 hover:text-slate-900 transition-colors font-medium"
                  data-testid={`settings-quick-${qa.id}`}
                >
                  {qa.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* No results message */}
      {isFiltering && matchCount === 0 && (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-sm bg-slate-50/50" data-testid="settings-no-results">
          <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-500 mb-1">No se encontraron ajustes</p>
          <p className="text-xs text-slate-400 mb-4">Prueba con otras palabras clave o limpia los filtros</p>
          <Button variant="outline" size="sm" onClick={clearSearch} className="rounded-sm text-xs uppercase" data-testid="settings-no-results-clear">
            <X className="w-3 h-3 mr-1" /> Limpiar busqueda
          </Button>
        </div>
      )}

      {/* ═══ SECTION: DATOS DEL TALLER ═══ */}
      {isSectionVisible("datos-taller") && (
        <div id="section-datos-taller" className={sectionClass("datos-taller")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Building2 className="w-4 h-4 text-red-600" /> Datos del Taller
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre del Taller</Label>
                  <Input value={config.workshop_name || ""} onChange={(e) => setConfig({...config, workshop_name: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
                  <Input value={config.email || ""} onChange={(e) => setConfig({...config, email: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Telefono</Label>
                  <Input value={config.phone || ""} onChange={(e) => setConfig({...config, phone: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-phone" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">WhatsApp General</Label>
                  <Input value={config.whatsapp_general_phone || ""} onChange={(e) => setConfig({...config, whatsapp_general_phone: e.target.value})}
                    placeholder="+34600000000" className="rounded-sm h-9 border-slate-200" data-testid="config-whatsapp-general" />
                  <p className="text-[10px] text-slate-400">Numero usado en derivaciones generales de posventa. Formato: +346XXXXXXXX. Si esta vacio se usara el telefono principal.</p>
                </div>
              </div>

              {/* Iter 151 — Jefe de Carrocería: contacto adicional en /habla-con-tu-asesor */}
              <div className="border border-amber-200 bg-amber-50/40 rounded-sm p-4 space-y-3" data-testid="bodyshop-contact-config">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-semibold text-amber-900">Jefe de Carrocería</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Aparece como tarjeta adicional en "Habla con tu asesor" cuando el cliente ha sido verificado.
                      Útil para consultas de chapa, pintura, partes o peritaciones.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!(config.bodyshop_contact?.active)}
                      onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), active: e.target.checked}})}
                      data-testid="bodyshop-active"
                    />
                    <span className="text-xs font-semibold text-slate-700">Activo</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nombre visible</Label>
                    <Input value={config.bodyshop_contact?.name || ""}
                      onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), name: e.target.value}})}
                      placeholder="Ej: Carlos Pérez" className="rounded-sm h-8 text-sm bg-white" data-testid="bodyshop-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cargo visible</Label>
                    <Input value={config.bodyshop_contact?.role || ""}
                      onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), role: e.target.value}})}
                      placeholder="Jefe de Carrocería" className="rounded-sm h-8 text-sm bg-white" data-testid="bodyshop-role" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Teléfono WhatsApp *</Label>
                    <Input value={config.bodyshop_contact?.phone || ""}
                      onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), phone: e.target.value}})}
                      placeholder="+34699111222" className="rounded-sm h-8 text-sm bg-white" data-testid="bodyshop-phone" />
                    <p className="text-[10px] text-slate-400">Sin teléfono, la tarjeta NO se mostrará aunque esté activa.</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Horario visible</Label>
                    <Input value={config.bodyshop_contact?.hours || ""}
                      onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), hours: e.target.value}})}
                      placeholder="L-V 9:00 a 18:00" className="rounded-sm h-8 text-sm bg-white" data-testid="bodyshop-hours" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Centro</Label>
                    <Input value={config.bodyshop_contact?.center || ""}
                      onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), center: e.target.value}})}
                      placeholder="Móstoles" className="rounded-sm h-8 text-sm bg-white" data-testid="bodyshop-center" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Texto de ayuda</Label>
                    <Input value={config.bodyshop_contact?.help_text || ""}
                      onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), help_text: e.target.value}})}
                      placeholder="Para consultas de chapa, pintura, partes o peritaciones." className="rounded-sm h-8 text-sm bg-white" data-testid="bodyshop-help" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mensaje predefinido WhatsApp</Label>
                  <Input value={config.bodyshop_contact?.wa_message_template || ""}
                    onChange={(e) => setConfig({...config, bodyshop_contact: {...(config.bodyshop_contact||{}), wa_message_template: e.target.value}})}
                    placeholder="Hola, soy {client_name}. Quería consultar con carrocería sobre mi vehículo {plate}."
                    className="rounded-sm h-8 text-sm bg-white" data-testid="bodyshop-wa-msg" />
                  <p className="text-[10px] text-slate-400">Variables admitidas: <code>{"{client_name}"}</code>, <code>{"{plate}"}</code>, <code>{"{vehicle_model}"}</code>.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Direccion</Label>
                  <Input value={config.address || ""} onChange={(e) => setConfig({...config, address: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-address" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre comercial (web)</Label>
                  <Input value={config.company_name || ""} onChange={(e) => setConfig({...config, company_name: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-company-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Iniciales logo</Label>
                  <Input value={config.company_short || ""} onChange={(e) => setConfig({...config, company_short: e.target.value})}
                    maxLength={3} className="rounded-sm h-9 border-slate-200 w-20" data-testid="config-company-short" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Tagline / Eslogan</Label>
                  <Input value={config.tagline || ""} onChange={(e) => setConfig({...config, tagline: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-tagline" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto legal (footer)</Label>
                  <Input value={config.legal_text || ""} onChange={(e) => setConfig({...config, legal_text: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-legal" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Descripcion footer</Label>
                  <Input value={config.footer_text || ""} onChange={(e) => setConfig({...config, footer_text: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="config-footer" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">Redes Sociales</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["facebook", "instagram", "twitter", "linkedin", "youtube"].map(net => (
                    <div key={net} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-20 capitalize">{net}</span>
                      <Input value={(config.social_media || {})[net] || ""} onChange={(e) => setConfig({...config, social_media: {...(config.social_media || {}), [net]: e.target.value}})}
                        placeholder={`URL de ${net}`} className="rounded-sm h-8 border-slate-200 text-xs flex-1" data-testid={`config-social-${net}`} />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: HORARIO ═══ */}
      {isSectionVisible("horario-trabajo") && (
        <div id="section-horario-trabajo" className={sectionClass("horario-trabajo")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Clock className="w-4 h-4 text-red-600" /> Horario de Trabajo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-[10px] text-slate-400 mb-2">Formato: <code className="bg-slate-100 px-1 rounded text-[10px]">07:00-18:00</code> o partido: <code className="bg-slate-100 px-1 rounded text-[10px]">07:00-14:00, 15:30-18:00</code></p>
              {DAYS.map((day) => (
                <div key={day.key}>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-slate-700 w-24">{day.label}</span>
                    <Input value={config.working_hours?.[day.key] || ""} onChange={(e) => updateHour(day.key, e.target.value)}
                      placeholder="07:00-18:00 o cerrado" className={`rounded-sm h-8 max-w-xs text-sm ${hourErrors[day.key] ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                      data-testid={`config-hours-${day.key}`} />
                  </div>
                  {hourErrors[day.key] && <p className="text-[10px] text-red-500 ml-28 mt-0.5" data-testid={`hours-error-${day.key}`}>{hourErrors[day.key]}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: SERVICIOS ═══ */}
      {isSectionVisible("servicios") && (
        <div id="section-servicios" className={sectionClass("servicios")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-red-600" /> Servicios Ofrecidos
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">Servicios disponibles en Recepcion Rapida y formulario publico. Ordena, activa/desactiva o elimina.</p>
              <div className="space-y-1.5" data-testid="services-list">
                {(config.services || [])
                  .sort((a, b) => ((typeof a === "object" ? a.order : 0) ?? 0) - ((typeof b === "object" ? b.order : 0) ?? 0))
                  .map((svc, i) => {
                    const name = typeof svc === "string" ? svc : svc.name;
                    const active = typeof svc === "object" ? svc.active !== false : true;
                    const services = config.services || [];
                    return (
                      <div key={i} className={`flex items-center gap-2 p-2 rounded-sm border ${active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`} data-testid={`service-row-${i}`}>
                        <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                        <span className="text-[10px] text-slate-400 w-5 text-center font-mono">{i + 1}</span>
                        <span className={`text-sm flex-1 ${active ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>{name}</span>
                        <Switch
                          checked={active}
                          onCheckedChange={v => {
                            const updated = services.map((s, idx) => {
                              if (idx !== i) return s;
                              if (typeof s === "string") return { name: s, active: v, order: idx };
                              return { ...s, active: v };
                            });
                            setConfig({ ...config, services: updated });
                          }}
                          data-testid={`service-toggle-${i}`}
                        />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={i === 0}
                          onClick={() => {
                            const arr = [...services];
                            [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                            const reordered = arr.map((s, idx) => typeof s === "string" ? { name: s, active: true, order: idx } : { ...s, order: idx });
                            setConfig({ ...config, services: reordered });
                          }}
                          data-testid={`service-up-${i}`}>
                          <ChevronUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={i === services.length - 1}
                          onClick={() => {
                            const arr = [...services];
                            [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                            const reordered = arr.map((s, idx) => typeof s === "string" ? { name: s, active: true, order: idx } : { ...s, order: idx });
                            setConfig({ ...config, services: reordered });
                          }}
                          data-testid={`service-down-${i}`}>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={() => {
                            const updated = services.filter((_, idx) => idx !== i)
                              .map((s, idx) => typeof s === "string" ? { name: s, active: true, order: idx } : { ...s, order: idx });
                            setConfig({ ...config, services: updated });
                          }}
                          data-testid={`service-delete-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
              </div>
              <div className="flex gap-2 mt-3">
                <Input id="new-service" placeholder="Nuevo servicio..." className="rounded-sm h-8 border-slate-200 text-sm flex-1" data-testid="new-service-input" />
                <Button variant="outline" size="sm" className="rounded-sm text-xs uppercase h-8"
                  onClick={() => {
                    const input = document.getElementById('new-service');
                    if (input.value.trim()) {
                      const current = config.services || [];
                      const newSvc = { name: input.value.trim(), active: true, order: current.length };
                      setConfig({ ...config, services: [...current, newSvc] });
                      input.value = '';
                    }
                  }}
                  data-testid="add-service-btn"><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: INTERVALO CITAS ═══ */}
      {isSectionVisible("intervalo-citas") && (
        <div id="section-intervalo-citas" className={sectionClass("intervalo-citas")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Timer className="w-4 h-4 text-red-600" /> Intervalo de Citas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">Tiempo entre citas consecutivas. Determina los huecos horarios disponibles.</p>
              <Select value={String(config.appointment_interval || 20)} onValueChange={v => setConfig({...config, appointment_interval: parseInt(v)})}>
                <SelectTrigger className="w-48 rounded-sm h-9 border-slate-200" data-testid="config-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVAL_OPTIONS.map(o => <SelectItem key={o} value={String(o)}>{o} minutos</SelectItem>)}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: MARCAS ═══ */}
      {isSectionVisible("marcas") && (
        <div id="section-marcas" className={sectionClass("marcas")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Car className="w-4 h-4 text-red-600" /> Marcas Oficiales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">Marcas que se muestran en la web publica y en el formulario de reserva.</p>
              <div className="flex flex-wrap gap-2">
                {(config.brands || []).map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-3 py-1.5 rounded-sm font-medium">
                    {b.name || b}
                    <button onClick={() => setConfig({...config, brands: config.brands.filter((_, idx) => idx !== i)})}
                      className="ml-1 text-slate-400 hover:text-red-600" data-testid={`remove-brand-${i}`}>&times;</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Input id="new-brand-name" placeholder="Nombre marca..." className="rounded-sm h-8 border-slate-200 text-sm flex-1" data-testid="new-brand-input" />
                <Input id="new-brand-logo" placeholder="URL logo (opcional)" className="rounded-sm h-8 border-slate-200 text-sm flex-1" />
                <Button variant="outline" size="sm" className="rounded-sm text-xs uppercase h-8"
                  onClick={() => {
                    const nameEl = document.getElementById('new-brand-name');
                    if (nameEl.value.trim()) {
                      const logoEl = document.getElementById('new-brand-logo');
                      setConfig({...config, brands: [...(config.brands || []), { name: nameEl.value.trim(), logo: logoEl.value.trim() }]});
                      nameEl.value = ''; logoEl.value = '';
                    }
                  }}
                  data-testid="add-brand-btn">Agregar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: UMBRALES ALERTAS ═══ */}
      {isSectionVisible("umbrales-alertas") && (
        <div id="section-umbrales-alertas" className={sectionClass("umbrales-alertas")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" /> Umbrales de Alertas y Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">Configura cuando se activan las alertas del dashboard de inteligencia de negocio.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Umbral saturacion (%)</Label>
                  <Input type="number" value={config.threshold_saturation ?? 80} onChange={e => setConfig({...config, threshold_saturation: parseInt(e.target.value) || 80})}
                    className="rounded-sm h-9 border-slate-200 w-24" data-testid="threshold-saturation" />
                  <p className="text-[10px] text-slate-400">Alerta cuando la ocupacion supere este %</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Umbral huecos muertos (%)</Label>
                  <Input type="number" value={config.threshold_dead_slots ?? 40} onChange={e => setConfig({...config, threshold_dead_slots: parseInt(e.target.value) || 40})}
                    className="rounded-sm h-9 border-slate-200 w-24" data-testid="threshold-dead-slots" />
                  <p className="text-[10px] text-slate-400">Alerta cuando el % de huecos vacios supere este valor</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Umbral presion de agenda (%)</Label>
                  <Input type="number" value={config.threshold_pressure ?? 80} onChange={e => setConfig({...config, threshold_pressure: parseInt(e.target.value) || 80})}
                    className="rounded-sm h-9 border-slate-200 w-24" data-testid="threshold-pressure" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Umbral pendientes (alertar desde)</Label>
                  <Input type="number" value={config.threshold_pending_warn ?? 5} onChange={e => setConfig({...config, threshold_pending_warn: parseInt(e.target.value) || 5})}
                    className="rounded-sm h-9 border-slate-200 w-24" data-testid="threshold-pending" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: NEGOCIO ═══ */}
      {isSectionVisible("negocio") && (
        <div id="section-negocio" className={sectionClass("negocio")}>
          <Card className="border-slate-200 rounded-sm" data-testid="business-config">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Gauge className="w-4 h-4 text-red-600" /> Negocio / Gerencia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">Parametros utilizados para calcular la prevision de ingresos en el dashboard de Gerencia.</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Ticket medio del taller (&euro;)</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} step={5} value={config.ticket_medio ?? 0}
                      onChange={e => setConfig({...config, ticket_medio: parseFloat(e.target.value) || 0})}
                      className="rounded-sm h-9 border-slate-200 w-32" data-testid="ticket-medio-input" />
                    <span className="text-xs text-slate-400">&euro; por cita confirmada</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Objetivo mensual de ingresos (&euro;)</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} step={500} value={config.objetivo_mensual_ingresos ?? 0}
                      onChange={e => setConfig({...config, objetivo_mensual_ingresos: parseFloat(e.target.value) || 0})}
                      className="rounded-sm h-9 border-slate-200 w-32" data-testid="objetivo-mensual-input" />
                    <span className="text-xs text-slate-400">&euro; / mes</span>
                  </div>
                  <p className="text-[10px] text-slate-400">Se escala automaticamente: semana=obj/4, trimestre=obj*3, ano=obj*12</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Umbral cliente VIP (visitas)</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={1} step={1} value={config.vip_threshold ?? 5}
                      onChange={e => setConfig({...config, vip_threshold: parseInt(e.target.value) || 5})}
                      className="rounded-sm h-9 border-slate-200 w-24" data-testid="vip-threshold-input" />
                    <span className="text-xs text-slate-400">visitas para ser VIP</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: EMAIL ═══ */}
      {isSectionVisible("email") && (
        <div id="section-email" className={sectionClass("email")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Mail className="w-4 h-4 text-red-600" /> Notificaciones por Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">Configura el remitente de los emails y activa o desactiva cada tipo de notificacion.</p>

              {/* Sender config */}
              <div className="space-y-3 pb-4 border-b border-slate-200">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Remitente de emails</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email remitente</Label>
                    <p className="text-[10px] text-slate-400 mb-1">Direccion desde la que salen los emails. Si vacio usa el valor del servidor.</p>
                    <Input value={config.sender_email || ""} onChange={e => setConfig({...config, sender_email: e.target.value})}
                      placeholder="Ej: citas@correo.ascauto.com" className="rounded-sm h-9 border-slate-200" data-testid="sender-email-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre del remitente</Label>
                    <p className="text-[10px] text-slate-400 mb-1">Nombre que aparece como remitente. Si vacio usa el valor del servidor.</p>
                    <Input value={config.sender_name || ""} onChange={e => setConfig({...config, sender_name: e.target.value})}
                      placeholder="Ej: Ascauto Taller Oficial" className="rounded-sm h-9 border-slate-200" data-testid="sender-name-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email de notificaciones internas</Label>
                  <p className="text-[10px] text-slate-400 mb-1">Recibe avisos de nuevas citas, cancelaciones, etc. Si vacio usa el email general del taller.</p>
                  <Input value={config.notification_email || ""} onChange={e => setConfig({...config, notification_email: e.target.value})}
                    placeholder="Ej: avisos@ascauto.com" className="rounded-sm h-9 border-slate-200 sm:w-1/2" data-testid="notification-email-input" />
                </div>
              </div>

              {/* Toggles */}
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Tipos de email activos</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Email de recepcion</p>
                    <p className="text-[10px] text-slate-500">Al solicitar una cita</p>
                  </div>
                  <Switch checked={config.email_reception_enabled ?? true}
                    onCheckedChange={v => setConfig({...config, email_reception_enabled: v})} data-testid="email-reception-toggle" />
                </div>
                <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Email de confirmacion</p>
                    <p className="text-[10px] text-slate-500">Cuando el asesor confirma la cita</p>
                  </div>
                  <Switch checked={config.email_confirmation_enabled ?? true}
                    onCheckedChange={v => setConfig({...config, email_confirmation_enabled: v})} data-testid="email-confirmation-toggle" />
                </div>
                <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Email de recordatorio</p>
                    <p className="text-[10px] text-slate-500">Recordatorio automatico antes de la cita</p>
                  </div>
                  <Switch checked={config.email_reminder_enabled ?? true}
                    onCheckedChange={v => setConfig({...config, email_reminder_enabled: v})} data-testid="email-reminder-toggle" />
                </div>
              </div>
              <div className="space-y-1 mt-3">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Horas de antelacion para recordatorio</Label>
                <p className="text-[10px] text-slate-400 mb-1">Separadas por coma. Ej: 48,24 para enviar a 48h y 24h antes</p>
                <Input value={(config.reminder_hours || [48, 24]).join(",")}
                  onChange={e => setConfig({...config, reminder_hours: e.target.value.split(",").map(v => parseInt(v.trim())).filter(v => v > 0)})}
                  className="rounded-sm h-9 border-slate-200 w-48" data-testid="reminder-hours-input" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: VEHICULOS CONFIG ═══ */}
      {isSectionVisible("vehiculos-config") && (
        <div id="section-vehiculos-config" className={sectionClass("vehiculos-config")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Car className="w-4 h-4 text-red-600" /> Configuracion de Vehiculos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Estados de vehiculo</Label>
                <p className="text-[10px] text-slate-400">Estados disponibles en el catalogo de vehiculos</p>
                <div className="flex flex-wrap gap-2">
                  {(config.vehicle_states || []).map((st, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-3 py-1.5 rounded-sm font-medium">
                      {st.label}
                      <button onClick={() => setConfig({...config, vehicle_states: config.vehicle_states.filter((_, idx) => idx !== i)})}
                        className="ml-1 text-slate-400 hover:text-red-600">&times;</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input id="new-vs-value" placeholder="valor (ej: disponible)" className="rounded-sm h-8 border-slate-200 text-sm flex-1" />
                  <Input id="new-vs-label" placeholder="Etiqueta (ej: Disponible)" className="rounded-sm h-8 border-slate-200 text-sm flex-1" />
                  <Button variant="outline" size="sm" className="rounded-sm text-xs uppercase h-8"
                    onClick={() => {
                      const val = document.getElementById('new-vs-value');
                      const lbl = document.getElementById('new-vs-label');
                      if (val.value.trim() && lbl.value.trim()) {
                        setConfig({...config, vehicle_states: [...(config.vehicle_states || []), { value: val.value.trim(), label: lbl.value.trim() }]});
                        val.value = ''; lbl.value = '';
                      }
                    }}
                    data-testid="add-vehicle-state-btn">Agregar</Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Tipos de vehiculo</Label>
                <div className="flex flex-wrap gap-2">
                  {(config.vehicle_types || []).map((vt, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-3 py-1.5 rounded-sm font-medium">
                      {vt.label}
                      <button onClick={() => setConfig({...config, vehicle_types: config.vehicle_types.filter((_, idx) => idx !== i)})}
                        className="ml-1 text-slate-400 hover:text-red-600">&times;</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input id="new-vt-value" placeholder="valor (ej: km0)" className="rounded-sm h-8 border-slate-200 text-sm flex-1" />
                  <Input id="new-vt-label" placeholder="Etiqueta (ej: KM0)" className="rounded-sm h-8 border-slate-200 text-sm flex-1" />
                  <Button variant="outline" size="sm" className="rounded-sm text-xs uppercase h-8"
                    onClick={() => {
                      const val = document.getElementById('new-vt-value');
                      const lbl = document.getElementById('new-vt-label');
                      if (val.value.trim() && lbl.value.trim()) {
                        setConfig({...config, vehicle_types: [...(config.vehicle_types || []), { value: val.value.trim(), label: lbl.value.trim() }]});
                        val.value = ''; lbl.value = '';
                      }
                    }}
                    data-testid="add-vehicle-type-btn">Agregar</Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Etiquetas comerciales</Label>
                <div className="flex flex-wrap gap-2">
                  {(config.vehicle_labels || []).map((lb, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-3 py-1.5 rounded-sm font-medium">
                      {lb}
                      <button onClick={() => setConfig({...config, vehicle_labels: config.vehicle_labels.filter((_, idx) => idx !== i)})}
                        className="ml-1 text-slate-400 hover:text-red-600">&times;</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input id="new-vl" placeholder="Nueva etiqueta..." className="rounded-sm h-8 border-slate-200 text-sm flex-1" />
                  <Button variant="outline" size="sm" className="rounded-sm text-xs uppercase h-8"
                    onClick={() => {
                      const el = document.getElementById('new-vl');
                      if (el.value.trim()) {
                        setConfig({...config, vehicle_labels: [...(config.vehicle_labels || []), el.value.trim()]});
                        el.value = '';
                      }
                    }}
                    data-testid="add-vehicle-label-btn">Agregar</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: MENSAJES FORMULARIO ═══ */}
      {isSectionVisible("mensajes-formulario") && (
        <div id="section-mensajes-formulario" className={sectionClass("mensajes-formulario")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <FileText className="w-4 h-4 text-red-600" /> Mensajes del Formulario de Reserva
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto legal del formulario</Label>
                <Input value={config.form_legal_text || ""} onChange={e => setConfig({...config, form_legal_text: e.target.value})}
                  className="rounded-sm h-9 border-slate-200" data-testid="form-legal-text" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Mensaje de exito tras reservar</Label>
                <Input value={config.form_success_message || ""} onChange={e => setConfig({...config, form_success_message: e.target.value})}
                  className="rounded-sm h-9 border-slate-200" data-testid="form-success-msg" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Mensaje cuando no hay horario</Label>
                <Input value={config.form_closed_message || ""} onChange={e => setConfig({...config, form_closed_message: e.target.value})}
                  className="rounded-sm h-9 border-slate-200" data-testid="form-closed-msg" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: CARGA AGENDA ═══ */}
      {isSectionVisible("carga-agenda") && (
        <div id="section-carga-agenda" className={sectionClass("carga-agenda")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-red-600" /> Indicador de Carga de Agenda
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[10px] text-slate-400">Define los umbrales de ocupacion para el indicador visual del calendario</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-green-600">Carga Baja (verde) hasta %</Label>
                  <Input type="number" min={10} max={100} value={config.load_thresholds?.green ?? 60}
                    onChange={e => setConfig({...config, load_thresholds: { ...(config.load_thresholds || {}), green: parseInt(e.target.value) || 60 }})}
                    className="rounded-sm h-9 border-slate-200" data-testid="threshold-green" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-amber-600">Carga Media (amarillo) hasta %</Label>
                  <Input type="number" min={10} max={100} value={config.load_thresholds?.yellow ?? 85}
                    onChange={e => setConfig({...config, load_thresholds: { ...(config.load_thresholds || {}), yellow: parseInt(e.target.value) || 85 }})}
                    className="rounded-sm h-9 border-slate-200" data-testid="threshold-yellow" />
                </div>
              </div>
              <p className="text-[9px] text-slate-400">Por encima del umbral amarillo se considera carga Alta (rojo)</p>
              <div className="border-t border-slate-200 pt-3 mt-3">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Cliente Habitual: minimo de visitas</Label>
                <Input type="number" min={1} max={50} value={config.habitual_client_threshold ?? 3}
                  onChange={e => setConfig({...config, habitual_client_threshold: parseInt(e.target.value) || 3})}
                  className="rounded-sm h-9 border-slate-200 w-32 mt-1" data-testid="habitual-threshold" />
                <p className="text-[9px] text-slate-400 mt-1">Numero minimo de visitas para mostrar el indicador "Cliente habitual" en el detalle de cita</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: HOME PUBLICA / HERO ═══ */}
      {isSectionVisible("home-publica") && (
        <div id="section-home-publica" className={sectionClass("home-publica")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-red-600" /> Home Publica / Hero Slider
                </CardTitle>
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={createSlide} data-testid="hero-add-slide-btn">
                  <Plus className="w-3 h-3 mr-1" /> Nuevo Slide
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">Gestiona los slides del hero rotatorio de la home publica. Cada slide puede tener imagen, titulos, botones y enlace de destino.</p>

              {/* Hero config toggles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[10px] uppercase text-slate-500 font-semibold">Autoplay</Label>
                  <Switch checked={(config.hero_autoplay ?? true)} onCheckedChange={v => setConfig({...config, hero_autoplay: v})} data-testid="hero-autoplay-toggle" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase text-slate-500 font-semibold">Velocidad (ms)</Label>
                  <Input type="number" value={config.hero_autoplay_interval ?? 5000} onChange={e => setConfig({...config, hero_autoplay_interval: parseInt(e.target.value) || 5000})} className="h-7 text-xs rounded-sm" data-testid="hero-interval-input" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[10px] uppercase text-slate-500 font-semibold">Flechas</Label>
                  <Switch checked={(config.hero_show_arrows ?? true)} onCheckedChange={v => setConfig({...config, hero_show_arrows: v})} data-testid="hero-arrows-toggle" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[10px] uppercase text-slate-500 font-semibold">Indicadores</Label>
                  <Switch checked={(config.hero_show_indicators ?? true)} onCheckedChange={v => setConfig({...config, hero_show_indicators: v})} data-testid="hero-indicators-toggle" />
                </div>
              </div>

              {/* Slides list */}
              {heroSlides.length === 0 && (
                <div className="text-center py-8 text-sm text-slate-400">
                  No hay slides creados. Pulsa "Nuevo Slide" para empezar.
                </div>
              )}
              <div className="space-y-2">
                {heroSlides.map((slide, idx) => (
                  <div key={slide.id} className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${slide.active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`} data-testid={`hero-slide-card-${idx}`}>
                    {/* Thumbnail */}
                    <div className="w-20 h-12 rounded-sm bg-slate-200 overflow-hidden flex-shrink-0 border border-slate-200">
                      {slide.image_url ? (
                        <img src={getImageUrl(slide.image_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-slate-300" /></div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-900 truncate">{slide.title}</span>
                        <Badge variant={slide.active ? "default" : "secondary"} className="text-[9px] h-4">{slide.active ? "Activo" : "Inactivo"}</Badge>
                        <span className="text-[10px] text-slate-400">#{idx + 1}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 truncate">{slide.subtitle}</p>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => toggleSlideActive(slide)} className={`p-1.5 rounded-sm transition-colors ${slide.active ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'}`} title={slide.active ? "Desactivar" : "Activar"} data-testid={`hero-slide-toggle-${idx}`}>
                        <Power className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => openEditSlide(slide)} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm transition-colors" data-testid={`hero-slide-edit-${idx}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteSlide(slide.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors" data-testid={`hero-slide-delete-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: SECCIONES WEB ═══ */}
      {isSectionVisible("secciones-web") && (
        <div id="section-secciones-web" className={sectionClass("secciones-web")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Eye className="w-4 h-4 text-red-600" /> Secciones Visibles en la Web
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">Activa o desactiva secciones enteras de la web publica.</p>
              {[
                { key: "hero", label: "Hero / Banner principal" },
                { key: "brands", label: "Marcas oficiales" },
                { key: "services", label: "Servicios" },
                { key: "advantages", label: "Ventajas" },
                { key: "testimonials", label: "Testimonios / Opiniones" },
                { key: "vehicles", label: "Vehículos" },
                { key: "centers", label: "Centros" },
                { key: "cta_final", label: "CTA final" },
              ].map(s => (
                <div key={s.key} className="flex items-center justify-between p-2.5 border border-slate-200 rounded-sm">
                  <span className="text-sm text-slate-700">{s.label}</span>
                  <Switch
                    checked={(config.sections_visibility || {})[s.key] ?? true}
                    onCheckedChange={v => setConfig({...config, sections_visibility: {...(config.sections_visibility || {}), [s.key]: v}})}
                    data-testid={`section-toggle-${s.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: INFORMES PDF ═══ */}
      {isSectionVisible("informes-pdf") && (
        <div id="section-informes-pdf" className={sectionClass("informes-pdf")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Gauge className="w-4 h-4 text-red-600" /> Configuracion de Informes PDF
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre empresa en informes</Label>
                  <Input value={config.report_company_name || ""} onChange={e => setConfig({...config, report_company_name: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="report-company" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Pie de informe</Label>
                  <Input value={config.report_footer_text || ""} onChange={e => setConfig({...config, report_footer_text: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="report-footer" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: PRIVACIDAD ═══ */}
      {isSectionVisible("privacidad") && (
        <div id="section-privacidad" className={sectionClass("privacidad")}>
          <Card className="border-slate-200 rounded-sm" data-testid="privacy-section">
            <CardHeader className="pb-3">
              <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-600" /> Privacidad y Proteccion de Datos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">Configura los textos legales visibles en formularios publicos. Estos textos se muestran automaticamente en "Habla con tu asesor", "Reservar cita" y solicitudes de contacto.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Responsable del tratamiento</Label>
                  <Input value={config.privacy_responsible_name || ""} onChange={e => setConfig({...config, privacy_responsible_name: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="privacy-responsible-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">CIF / datos corporativos</Label>
                  <Input value={config.privacy_responsible_cif || ""} onChange={e => setConfig({...config, privacy_responsible_cif: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="privacy-responsible-cif" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email derechos RGPD</Label>
                  <Input value={config.privacy_contact_email || ""} onChange={e => setConfig({...config, privacy_contact_email: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="privacy-contact-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">URL politica de privacidad</Label>
                  <Input value={config.privacy_policy_url || ""} onChange={e => setConfig({...config, privacy_policy_url: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="privacy-policy-url" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto breve de privacidad (general)</Label>
                <textarea value={config.privacy_brief_text || ""} onChange={e => setConfig({...config, privacy_brief_text: e.target.value})}
                  className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" data-testid="privacy-brief-text" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto formulario "Habla con tu asesor"</Label>
                <textarea value={config.privacy_form_text_advisor || ""} onChange={e => setConfig({...config, privacy_form_text_advisor: e.target.value})}
                  className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" data-testid="privacy-text-advisor" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto formulario "Reservar cita"</Label>
                <textarea value={config.privacy_form_text_appointment || ""} onChange={e => setConfig({...config, privacy_form_text_appointment: e.target.value})}
                  className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" data-testid="privacy-text-appointment" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto formulario "Solicitud de contacto"</Label>
                <textarea value={config.privacy_form_text_contact || ""} onChange={e => setConfig({...config, privacy_form_text_contact: e.target.value})}
                  className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" data-testid="privacy-text-contact" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Mensaje error validacion publica</Label>
                <textarea value={config.privacy_error_generic || ""} onChange={e => setConfig({...config, privacy_error_generic: e.target.value})}
                  className="w-full border border-slate-200 rounded-sm p-2 text-sm h-12 resize-none" data-testid="privacy-error-generic" />
                <p className="text-[10px] text-slate-400">Mensaje mostrado cuando la validacion matricula+telefono falla en flujos publicos.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto checkbox obligatorio</Label>
                <Input value={config.privacy_checkbox_text || ""} onChange={e => setConfig({...config, privacy_checkbox_text: e.target.value})}
                  className="rounded-sm h-9 border-slate-200" data-testid="privacy-checkbox-text"
                  placeholder="He leido y acepto la Politica de privacidad" />
                <p className="text-[10px] text-slate-400">Texto del checkbox obligatorio que aparece en todos los formularios publicos.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto comunicaciones comerciales (opcional)</Label>
                <textarea value={config.privacy_commercial_text || ""} onChange={e => setConfig({...config, privacy_commercial_text: e.target.value})}
                  className="w-full border border-slate-200 rounded-sm p-2 text-sm h-12 resize-none" data-testid="privacy-commercial-text" />
                <p className="text-[10px] text-slate-400">Casilla opcional y separada para comunicaciones comerciales. Se muestra en formularios de contacto y solicitud de atencion.</p>
              </div>

              {/* Cookie banner config */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">Banner de cookies</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Titulo del banner</Label>
                  <Input value={config.cookie_banner_title || ""} onChange={e => setConfig({...config, cookie_banner_title: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="cookie-banner-title" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Boton aceptar</Label>
                  <Input value={config.cookie_banner_accept || ""} onChange={e => setConfig({...config, cookie_banner_accept: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="cookie-banner-accept" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto del banner de cookies</Label>
                <textarea value={config.cookie_banner_text || ""} onChange={e => setConfig({...config, cookie_banner_text: e.target.value})}
                  className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" data-testid="cookie-banner-text" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Boton rechazar</Label>
                  <Input value={config.cookie_banner_reject || ""} onChange={e => setConfig({...config, cookie_banner_reject: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="cookie-banner-reject" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Boton configurar</Label>
                  <Input value={config.cookie_banner_configure || ""} onChange={e => setConfig({...config, cookie_banner_configure: e.target.value})}
                    className="rounded-sm h-9 border-slate-200" data-testid="cookie-banner-configure" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: ASESORES ═══ */}
      {isSectionVisible("asesores") && (
        <div id="section-asesores" className={sectionClass("asesores")}>
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-red-600" /> Asesores de Servicio
                </CardTitle>
                <Button size="sm" onClick={() => { setEditAdv(null); setAdvForm({ name: "", center: "ambos", active: true, order: advisors.length + 1 }); setAdvDialog(true); }}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-8 px-3" data-testid="add-advisor-btn">
                  <Plus className="w-3 h-3 mr-1" /> Nuevo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">Los asesores activos determinan la capacidad del taller por franja horaria.</p>

              {/* Advisor selection config */}
              <div className="p-4 mb-4 border border-slate-200 rounded-sm bg-slate-50 space-y-3" data-testid="advisor-selection-config">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Seleccion de asesor en web publica</p>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config?.advisor_selection_enabled ?? false}
                      onCheckedChange={v => setConfig(prev => ({ ...prev, advisor_selection_enabled: v }))}
                      data-testid="advisor-selection-enabled"
                    />
                    <Label className="text-xs text-slate-700">Activar seleccion de asesor</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config?.advisor_selection_required ?? false}
                      onCheckedChange={v => setConfig(prev => ({ ...prev, advisor_selection_required: v }))}
                      data-testid="advisor-selection-required"
                    />
                    <Label className="text-xs text-slate-700">Obligatorio</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config?.advisor_show_no_preference ?? true}
                      onCheckedChange={v => setConfig(prev => ({ ...prev, advisor_show_no_preference: v }))}
                      data-testid="advisor-show-no-preference"
                    />
                    <Label className="text-xs text-slate-700">Mostrar "Sin preferencia"</Label>
                  </div>
                </div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mt-4">Asesor en emails de cita</p>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={config?.email_show_advisor_block ?? true} onCheckedChange={v => setConfig(prev => ({ ...prev, email_show_advisor_block: v }))} data-testid="email-show-advisor-block" />
                    <Label className="text-xs text-slate-700">Mostrar bloque asesor</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={config?.email_show_advisor_photo ?? true} onCheckedChange={v => setConfig(prev => ({ ...prev, email_show_advisor_photo: v }))} data-testid="email-show-advisor-photo" />
                    <Label className="text-xs text-slate-700">Mostrar foto</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={config?.email_show_advisor_email ?? true} onCheckedChange={v => setConfig(prev => ({ ...prev, email_show_advisor_email: v }))} data-testid="email-show-advisor-email" />
                    <Label className="text-xs text-slate-700">Mostrar email</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={config?.email_show_advisor_phone ?? true} onCheckedChange={v => setConfig(prev => ({ ...prev, email_show_advisor_phone: v }))} data-testid="email-show-advisor-phone" />
                    <Label className="text-xs text-slate-700">Mostrar telefono</Label>
                  </div>
                </div>
                <div className="space-y-1 mt-2">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Texto sin asesor asignado</Label>
                  <Input value={config?.email_no_advisor_text ?? "Tu asesor de servicio se asignara en el momento de tu llegada."} onChange={e => setConfig(prev => ({ ...prev, email_no_advisor_text: e.target.value }))} className="h-8 text-xs rounded-sm" data-testid="email-no-advisor-text" />
                </div>
              </div>

              <div className="space-y-2">
                {advisors.map(a => (
                  <div key={a.id} className={`flex items-center justify-between p-3 rounded-sm border ${a.active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`} data-testid={`advisor-row-${a.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/advisors/${a.id}/avatar`} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{a.name}</p>
                        <p className="text-xs text-slate-500">
                          {a.center === "ambos" ? "Todos los centros" : centers.find(c => c.slug === a.center)?.name || a.center}
                          {!a.active && <span className="ml-1 text-red-500 font-semibold">(Inactivo)</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditAdv(a)} className="text-xs h-7" data-testid={`edit-advisor-${a.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteAdvisor(a.id)} className="text-xs h-7 text-red-600" data-testid={`delete-advisor-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
                {advisors.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No hay asesores configurados</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══ SECTION: AUDITORIA ═══ */}
      {isSectionVisible("auditoria") && (
        <div id="section-auditoria" className={sectionClass("auditoria")}>
          <Card className="border-slate-200 rounded-sm" data-testid="audit-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-red-600" /> Registro de Auditoria
                </CardTitle>
                <span className="text-[10px] text-slate-400 font-mono" data-testid="audit-count">
                  {(auditSearch || auditActionFilter !== "all" || auditActorFilter !== "all" || auditTargetFilter !== "all" || auditDateFilter !== "all" || auditIpFilter)
                    ? `${auditTotal} resultados filtrados`
                    : `${auditTotal} registros`}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {/* Audit Search + Filters */}
              <div className="space-y-2 mb-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <Input
                      value={auditSearch}
                      onChange={e => setAuditSearch(e.target.value)}
                      placeholder="Buscar actor, accion, email, IP..."
                      className="pl-8 h-8 text-xs rounded-sm border-slate-200"
                      data-testid="audit-search-input"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Select value={auditActionFilter} onValueChange={setAuditActionFilter}>
                      <SelectTrigger className="h-8 w-auto min-w-[110px] text-xs rounded-sm border-slate-200" data-testid="audit-filter-action">
                        <SelectValue placeholder="Accion" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las acciones</SelectItem>
                        {auditAvailableActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Select value={auditActorFilter} onValueChange={setAuditActorFilter}>
                      <SelectTrigger className="h-8 w-auto min-w-[100px] text-xs rounded-sm border-slate-200" data-testid="audit-filter-actor">
                        <SelectValue placeholder="Actor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los actores</SelectItem>
                        {[...new Map(auditAvailableActors.map(a => [a.id, a])).values()].map(a =>
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>

                    <Select value={auditTargetFilter} onValueChange={setAuditTargetFilter}>
                      <SelectTrigger className="h-8 w-auto min-w-[90px] text-xs rounded-sm border-slate-200" data-testid="audit-filter-target">
                        <SelectValue placeholder="Target" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="user">Usuario</SelectItem>
                        <SelectItem value="role">Rol</SelectItem>
                        <SelectItem value="session">Sesion</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={auditDateFilter} onValueChange={setAuditDateFilter}>
                      <SelectTrigger className="h-8 w-auto min-w-[100px] text-xs rounded-sm border-slate-200" data-testid="audit-filter-date">
                        <SelectValue placeholder="Fecha" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Cualquier fecha</SelectItem>
                        <SelectItem value="today">Hoy</SelectItem>
                        <SelectItem value="7d">Ultimos 7 dias</SelectItem>
                        <SelectItem value="30d">Ultimos 30 dias</SelectItem>
                      </SelectContent>
                    </Select>

                    <button
                      onClick={() => setAuditSortOrder(v => v === "desc" ? "asc" : "desc")}
                      className="h-8 px-2.5 text-xs rounded-sm border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-1 text-slate-600 font-medium"
                      data-testid="audit-sort-toggle"
                    >
                      {auditSortOrder === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                      {auditSortOrder === "desc" ? "Recientes" : "Antiguos"}
                    </button>

                    {(auditSearch || auditActionFilter !== "all" || auditActorFilter !== "all" || auditTargetFilter !== "all" || auditDateFilter !== "all" || auditIpFilter) && (
                      <Button variant="ghost" size="sm" className="h-8 text-xs text-red-600 px-2" data-testid="audit-clear-filters"
                        onClick={() => { setAuditSearch(""); setAuditActionFilter("all"); setAuditActorFilter("all"); setAuditTargetFilter("all"); setAuditDateFilter("all"); setAuditIpFilter(""); }}>
                        <X className="w-3 h-3 mr-1" /> Limpiar
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Log entries */}
              <div className="space-y-1.5 max-h-[500px] overflow-y-auto" data-testid="audit-list">
                {auditLogs.map((log, i) => (
                  <div key={i} className="flex items-start gap-3 p-2.5 border border-slate-100 rounded-sm text-[11px] hover:bg-slate-50">
                    <div className="flex-shrink-0 w-20 text-right">
                      <p className="font-mono text-slate-400">{(() => { const p = log.timestamp?.slice(0,10).split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : log.timestamp?.slice(0,10); })()}</p>
                      <p className="font-mono text-slate-300">{log.timestamp?.slice(11, 16)}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700">
                        <span className="font-semibold">{log.actor_name || "Sistema"}</span>
                        <span className="text-slate-400"> ({log.actor_role}) </span>
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded-sm font-mono text-[10px]">{log.action}</span>
                        <span className="text-slate-400"> &rarr; </span>
                        <span className="font-medium">{log.target_type}</span>
                        {log.ip && <span className="text-slate-300 ml-2 font-mono text-[9px]">IP: {log.ip}</span>}
                      </p>
                      {log.details && <p className="text-slate-400 truncate">{log.details}</p>}
                      {log.before && <p className="text-red-400 truncate">Antes: {JSON.stringify(log.before)}</p>}
                      {log.after && <p className="text-green-500 truncate">Despues: {JSON.stringify(log.after)}</p>}
                    </div>
                  </div>
                ))}
                {auditLogs.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-6" data-testid="audit-no-results">
                    {(auditSearch || auditActionFilter !== "all" || auditActorFilter !== "all" || auditTargetFilter !== "all" || auditDateFilter !== "all" || auditIpFilter)
                      ? "No se han encontrado registros con los filtros aplicados"
                      : "Sin registros de auditoria"}
                  </p>
                )}
              </div>

              {/* Pagination */}
              {auditTotal > 30 && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-slate-400">Pag. {auditPage} de {Math.ceil(auditTotal / 30)}</span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={auditPage <= 1} onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                      className="text-xs h-7 rounded-sm" data-testid="audit-prev-page">Anterior</Button>
                    <Button variant="outline" size="sm" disabled={auditPage >= Math.ceil(auditTotal / 30)} onClick={() => setAuditPage(p => p + 1)}
                      className="text-xs h-7 rounded-sm" data-testid="audit-next-page">Siguiente</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Advisor Dialog */}
      <Dialog open={advDialog} onOpenChange={setAdvDialog}>
        <DialogContent className="max-w-md rounded-sm" data-testid="advisor-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">{editAdv ? "Editar Asesor" : "Nuevo Asesor"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            {/* Photo upload area */}
            {editAdv && (
              <div className="flex items-center gap-4" data-testid="advisor-photo-section">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {currentAdvisorHasManualPhoto ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/advisors/${editAdv}/photo/view?t=${Date.now()}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/advisors/${editAdv}/avatar`} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full whitespace-nowrap ${currentAdvisorHasManualPhoto ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`} data-testid="advisor-photo-badge">
                    {currentAdvisorHasManualPhoto ? 'Foto manual' : 'Avatar auto'}
                  </span>
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Foto (JPG/PNG, max 500KB)</Label>
                  <Input type="file" accept=".jpg,.jpeg,.png" className="h-8 text-xs rounded-sm" data-testid="advisor-photo-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData(); fd.append("file", file);
                      try {
                        await axiosAuth().post(`/advisors/${editAdv}/photo`, fd);
                        setCurrentAdvisorHasManualPhoto(true);
                        toast.success("Foto actualizada");
                        fetchAdvisors();
                      } catch (err) { toast.error(err.response?.data?.detail || "Error al subir foto"); }
                    }} />
                  {currentAdvisorHasManualPhoto && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm("Eliminar la foto y volver al avatar corporativo?")) return;
                        try {
                          await axiosAuth().delete(`/advisors/${editAdv}/photo`);
                          setCurrentAdvisorHasManualPhoto(false);
                          toast.success("Foto eliminada. Se usara el avatar corporativo.");
                          fetchAdvisors();
                        } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar foto"); }
                      }}
                      className="text-[10px] text-red-500 hover:text-red-700 font-medium transition-colors flex items-center gap-1"
                      data-testid="advisor-delete-photo-btn"
                    >
                      <Trash2 className="w-3 h-3" /> Eliminar foto
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre *</Label>
                <Input value={advForm.name} onChange={e => setAdvForm({...advForm, name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="advisor-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Centro</Label>
                <Select value={advForm.center} onValueChange={v => setAdvForm({...advForm, center: v})}>
                  <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="advisor-center-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ambos">Todos los centros</SelectItem>
                    {centers.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Cargo</Label>
                <Input value={advForm.title || ""} onChange={e => setAdvForm({...advForm, title: e.target.value})} className="rounded-sm h-9 border-slate-200" placeholder="Asesor de servicio" data-testid="advisor-title-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
                <Input type="email" value={advForm.email || ""} onChange={e => setAdvForm({...advForm, email: e.target.value})} className="rounded-sm h-9 border-slate-200" placeholder="asesor@correo.ascauto.com" data-testid="advisor-email-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Telefono</Label>
                <Input value={advForm.phone || ""} onChange={e => setAdvForm({...advForm, phone: e.target.value})} className="rounded-sm h-9 border-slate-200" placeholder="916003800" data-testid="advisor-phone-input" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch checked={advForm.active} onCheckedChange={v => setAdvForm({...advForm, active: v})} data-testid="advisor-active-switch" />
                <Label className="text-xs text-slate-600">Activo</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-600">Orden</Label>
                <Input type="number" value={advForm.order} onChange={e => setAdvForm({...advForm, order: parseInt(e.target.value) || 0})} className="rounded-sm h-8 border-slate-200 w-16 text-center text-xs" data-testid="advisor-order-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvDialog(false)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleSaveAdvisor} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="save-advisor-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ SECTION: ROLES ═══ */}
      {isSectionVisible("roles") && (
        <div id="section-roles" className={sectionClass("roles")}>
          <Card className="border-slate-200 rounded-sm" data-testid="roles-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
                  <Shield className="w-4 h-4 text-red-600" /> Roles y Permisos
                </CardTitle>
                <Button size="sm" onClick={() => { setEditRole(null); setRoleForm({ name: "", slug: "", level: 10, permissions: {}, active: true }); setRoleDialog(true); }}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-8 px-3" data-testid="add-role-btn">
                  <Plus className="w-3 h-3 mr-1" /> Nuevo Rol
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">Configura los roles del sistema y sus permisos por modulo. Los roles del sistema no se pueden eliminar.</p>
              <div className="space-y-2" data-testid="roles-list">
                {roles.map(r => (
                  <div key={r.id} className={`flex items-center gap-3 p-3 rounded-sm border ${r.active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`} data-testid={`role-row-${r.slug}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900">{r.name}</span>
                        <span className="text-[10px] text-slate-400 font-mono">({r.slug})</span>
                        {r.is_system && <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm font-medium">SISTEMA</span>}
                        {!r.active && <span className="text-[9px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded-sm font-medium">INACTIVO</span>}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Nivel: {r.level} &middot; {Object.keys(r.permissions || {}).length} modulos
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={r.active}
                        onCheckedChange={() => toggleRoleActive(r)}
                        data-testid={`role-toggle-${r.slug}`}
                        disabled={r.slug === "admin" || r.slug === "super_admin"}
                      />
                      <Button variant="ghost" size="sm" onClick={() => openEditRole(r)} className="text-xs h-7" data-testid={`edit-role-${r.slug}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      {!r.is_system && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(r.id)}
                          className={`text-xs h-7 ${roleDeleteConfirm === r.id ? "text-white bg-red-600 hover:bg-red-700" : "text-red-600"}`}
                          data-testid={`delete-role-${r.slug}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> {roleDeleteConfirm === r.id ? "Confirmar" : ""}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {roles.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No hay roles configurados</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Role Dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">{editRole ? "Editar Rol" : "Nuevo Rol"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1 col-span-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre *</Label>
                <Input value={roleForm.name} onChange={e => {
                  const name = e.target.value;
                  setRoleForm(prev => ({
                    ...prev,
                    name,
                    slug: editRole ? prev.slug : name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")
                  }));
                }} className="rounded-sm h-9 border-slate-200" data-testid="role-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Slug</Label>
                <Input value={roleForm.slug} onChange={e => setRoleForm({...roleForm, slug: e.target.value})}
                  disabled={!!editRole} className="rounded-sm h-9 border-slate-200 font-mono text-xs" data-testid="role-slug-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nivel (1-100)</Label>
                <Input type="number" min={1} max={100} value={roleForm.level} onChange={e => setRoleForm({...roleForm, level: parseInt(e.target.value) || 10})}
                  className="rounded-sm h-9 border-slate-200 w-24" data-testid="role-level-input" />
              </div>
            </div>

            {/* Permissions matrix */}
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Permisos por Modulo</Label>
              <p className="text-[10px] text-slate-400">Marca las acciones permitidas para cada modulo del sistema</p>
            </div>
            <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="permissions-matrix">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-3 py-2 font-semibold text-slate-600 uppercase tracking-wider">Modulo</th>
                    {actions.map(a => (
                      <th key={a} className="text-center px-2 py-2 font-semibold text-slate-600 uppercase tracking-wider w-20">{a}</th>
                    ))}
                    <th className="text-center px-2 py-2 font-semibold text-slate-600 uppercase tracking-wider w-16">Todo</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map(mod => {
                    const modPerms = roleForm.permissions[mod.key] || [];
                    const allChecked = actions.every(a => modPerms.includes(a));
                    return (
                      <tr key={mod.key} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-700">{mod.label}</td>
                        {actions.map(a => (
                          <td key={a} className="text-center px-2 py-2">
                            <Checkbox
                              checked={modPerms.includes(a)}
                              onCheckedChange={() => togglePermission(mod.key, a)}
                              data-testid={`perm-${mod.key}-${a}`}
                            />
                          </td>
                        ))}
                        <td className="text-center px-2 py-2">
                          <Checkbox
                            checked={allChecked}
                            onCheckedChange={() => toggleModuleAll(mod.key)}
                            data-testid={`perm-${mod.key}-all`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRoleDialog(false); setRoleSaveConfirm(false); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleSaveRole}
              className={`rounded-sm text-xs uppercase font-semibold ${roleSaveConfirm ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-900 hover:bg-slate-800"}`}
              data-testid="save-role-btn">
              {roleSaveConfirm ? "Confirmar cambios" : "Guardar Rol"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hero Slide Edit Dialog */}
      <Dialog open={heroSlideDialog} onOpenChange={setHeroSlideDialog}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="hero-slide-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">Editar Slide</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Preview */}
            {slideForm.image_url && (
              <div className="relative w-full h-32 rounded-sm overflow-hidden bg-slate-900">
                <img src={getImageUrl(slideForm.image_url)} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: `rgba(15,23,42,${(slideForm.overlay_opacity || 70) / 100})` }} />
                <div className="absolute bottom-2 left-3 z-10">
                  <p className="text-sm font-bold" style={{ color: slideForm.text_color === 'dark' ? '#0f172a' : '#ffffff' }}>{slideForm.title}</p>
                  <p className="text-[10px]" style={{ color: slideForm.text_color === 'dark' ? '#475569' : '#cbd5e1' }}>{slideForm.subtitle?.slice(0, 60)}</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Titulo</Label>
                <Input value={slideForm.title || ""} onChange={e => setSlideForm({...slideForm, title: e.target.value})} className="h-8 text-xs rounded-sm" data-testid="slide-title-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Texto auxiliar</Label>
                <Input value={slideForm.aux_text || ""} onChange={e => setSlideForm({...slideForm, aux_text: e.target.value})} placeholder="Ej: Servicio oficial Stellantis" className="h-8 text-xs rounded-sm" data-testid="slide-aux-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Subtitulo</Label>
              <textarea value={slideForm.subtitle || ""} onChange={e => setSlideForm({...slideForm, subtitle: e.target.value})} rows={2} className="w-full border border-slate-200 rounded-sm text-xs p-2 resize-none" data-testid="slide-subtitle-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">URL Imagen Desktop</Label>
              <div className="flex gap-2">
                <Input value={slideForm.image_url || ""} onChange={e => setSlideForm({...slideForm, image_url: e.target.value})} placeholder="https://... o subir imagen" className="h-8 text-xs rounded-sm flex-1" data-testid="slide-image-url-input" />
                <label className="flex items-center gap-1 px-2 h-8 text-[10px] text-slate-600 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50 transition-colors">
                  <Upload className="w-3 h-3" /> Subir
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append("file", file);
                    try {
                      const res = await axiosAuth().post(`/hero-slides/${editSlide}/image?variant=desktop`, fd);
                      setSlideForm(prev => ({...prev, image_url: res.data.url}));
                      toast.success("Imagen subida");
                    } catch { toast.error("Error al subir imagen"); }
                  }} />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Principal - Texto</Label>
                <Input value={slideForm.cta_primary_text || ""} onChange={e => setSlideForm({...slideForm, cta_primary_text: e.target.value})} placeholder="Reservar Cita" className="h-8 text-xs rounded-sm" data-testid="slide-cta1-text" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Principal - Enlace</Label>
                <Input value={slideForm.cta_primary_link || ""} onChange={e => setSlideForm({...slideForm, cta_primary_link: e.target.value})} placeholder="/cita" className="h-8 text-xs rounded-sm" data-testid="slide-cta1-link" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Secundario - Texto</Label>
                <Input value={slideForm.cta_secondary_text || ""} onChange={e => setSlideForm({...slideForm, cta_secondary_text: e.target.value})} placeholder="Ver Vehiculos" className="h-8 text-xs rounded-sm" data-testid="slide-cta2-text" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CTA Secundario - Enlace</Label>
                <Input value={slideForm.cta_secondary_link || ""} onChange={e => setSlideForm({...slideForm, cta_secondary_link: e.target.value})} placeholder="/vehiculos" className="h-8 text-xs rounded-sm" data-testid="slide-cta2-link" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Color texto</Label>
                <Select value={slideForm.text_color || "white"} onValueChange={v => setSlideForm({...slideForm, text_color: v})}>
                  <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="slide-text-color"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="white">Claro (sobre oscuro)</SelectItem>
                    <SelectItem value="dark">Oscuro (sobre claro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Opacidad overlay</Label>
                <Input type="number" min={0} max={100} value={slideForm.overlay_opacity ?? 70} onChange={e => setSlideForm({...slideForm, overlay_opacity: parseInt(e.target.value) || 0})} className="h-8 text-xs rounded-sm" data-testid="slide-overlay-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Orden</Label>
                <Input type="number" value={slideForm.order ?? 0} onChange={e => setSlideForm({...slideForm, order: parseInt(e.target.value) || 0})} className="h-8 text-xs rounded-sm" data-testid="slide-order-input" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={slideForm.active ?? true} onCheckedChange={v => setSlideForm({...slideForm, active: v})} data-testid="slide-active-toggle" />
              <Label className="text-xs text-slate-600">Slide activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHeroSlideDialog(false)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={saveSlide} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="save-slide-btn">Guardar Slide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Back to top button (when filtering) */}
      {isFiltering && matchCount > 0 && (
        <div className="fixed bottom-6 right-6 z-40">
          <Button
            size="sm"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-full w-10 h-10 p-0 shadow-lg"
            data-testid="settings-back-to-top"
          >
            <ArrowUp className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
